import {
  faDownload,
  faFileLines,
  faFileSlash,
  faSpinnerThird,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Document } from "@kolibrisoftware/customerportal-api";
import {
  Button,
  ButtonThemes,
  ContentSection,
} from "@kolibrisoftware/customerportal-ui";
import { useTranslation } from "@kolibrisoftware/hooks-util";
import { PageLoadingStatus } from "enums/page-loading-status";
import { createErrorToast } from "helpers/create-new-toast";
import { useToasts } from "hooks/useToast";
import { useTheme } from "injectors/theme";
import JSZip from "jszip";
import { FC, useCallback, useMemo, useState } from "react";
import ActivityItem from "shared/components/activity-item";
import HeaderSection from "shared/ui/header-section";
import LoadingScreen from "shared/ui/loading-screen";
import ScreenWrapper from "shared/ui/screen-wrapper";
import { documentApi } from "store/api";
import { useDispatch, useSelector } from "store/helpers";
import { useStyles } from "./styles";

type Props = {};

const DocumentsScreen: FC<Props> = props => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const styles = useStyles({ theme });
  const t = useTranslation();
  const loadingStatus = useSelector(state => state.dossier.loadingStatus);
  const toasts = useToasts();
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const documents = useSelector(state =>
    state.dossier.documents.filter(item => item)
  );

  const assignmentName = useSelector(
    state => state.dossier.assignmentDetails.displayName
  );

  const translations = useMemo(
    () => ({
      headerTitle: t("documents.headerTitle"),
      headerDescription: t("documents.headerDescription"),
      noDocuments: t("documents.noDocuments"),
      downloadActionLabel: t("common.downloadAction"),
      downloadAllLabel: t("documents.downloadAll"),
      sharedByRealtor: t("documents.sharedByRealtor"),
      sharedByYou: t("documents.sharedByYou"),
    }),
    [t]
  );

  const sharedByRealtorDocuments: Document[] = useMemo(() => {
    return (
      documents?.filter(doc => doc.ownerAssignmentRelationId === null) ?? []
    );
  }, [documents]);

  const sharedByUserDocuments: Document[] = useMemo(() => {
    return (
      documents?.filter(doc => doc.ownerAssignmentRelationId !== null) ?? []
    );
  }, [documents]);

  const handleDownload = useCallback(
    async (documentId: string) => {
      try {
        const downloadLink = await dispatch(
          documentApi.downloadDocument({ documentId: documentId })
        ).unwrap();

        if (downloadLink.tempAccessDownloadUrl) {
          window.open(downloadLink.tempAccessDownloadUrl, "_self");
        } else {
          const toast = createErrorToast("Niet beschikbaar");
          toasts.addToast(toast);
        }
      } catch (error) {
        throw error;
      }
    },
    [dispatch, toasts]
  );

  const handleDownloadAll = useCallback(async () => {
    try {
      setIsDownloadingAll(true);
      const validDocuments = documents.filter(doc => Boolean(doc.id));

      if (validDocuments.length === 0) {
        const toast = createErrorToast("Geen documenten beschikbaar");
        toasts.addToast(toast);
        return;
      }

      // Set up ZIP
      const zip = new JSZip();
      const failed: string[] = [];

      // Loop through documents, fetch blobs and add to zip
      for (const document of validDocuments) {
        if (!document.id) continue;
        const fileName = document.fileName ?? `document-${document.id}`;

        try {
          const downloadLink = await dispatch(
            documentApi.downloadDocument({ documentId: document.id })
          ).unwrap();

          if (downloadLink.tempAccessDownloadUrl) {
            const response = await fetch(downloadLink.tempAccessDownloadUrl);
            const blob = await response.blob();

            // Push blob to zip
            zip.file(fileName, blob);
          } else {
            failed.push(fileName);
          }
        } catch {
          // Keep track of failed downloads
          failed.push(fileName);
        }
      }

      if (failed.length > 0) {
        throw Error();
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);

      // Create fake download link and click it
      const a = window.document.createElement("a");
      a.href = url;
      a.download = `${assignmentName ?? "all"}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const toast = createErrorToast(t("documents.downloadAllFailed"));
      toasts.addToast(toast);
    } finally {
      setIsDownloadingAll(false);
    }
  }, [assignmentName, documents, dispatch, t, toasts]);

  return (
    <ScreenWrapper>
      {(loadingStatus === PageLoadingStatus.Loading ||
        loadingStatus === PageLoadingStatus.Pending) && <LoadingScreen />}
      {loadingStatus === PageLoadingStatus.Success && (
        <>
          {!documents.length && (
            <ContentSection>
              <div className={styles.noDocumentsContainer}>
                <FontAwesomeIcon
                  icon={faFileSlash}
                  className={styles.noDocumentsIcon}
                />
                <h3 className={styles.noDocumentsText}>
                  {translations.noDocuments}
                </h3>
              </div>
            </ContentSection>
          )}
          {!!documents.length && (
            <>
              <HeaderSection
                title={translations.headerTitle}
                description={translations.headerDescription}
              >
                {Number(documents?.length) > 0 && (
                  <Button
                    onClick={handleDownloadAll}
                    buttonTheme={ButtonThemes.Primary}
                    size="m"
                    className={styles.downloadAllButton}
                    disabled={isDownloadingAll}
                  >
                    <FontAwesomeIcon
                      icon={isDownloadingAll ? faSpinnerThird : faDownload}
                      spin={isDownloadingAll}
                    />
                    {translations.downloadAllLabel}
                  </Button>
                )}
              </HeaderSection>

              {Number(sharedByRealtorDocuments?.length) > 0 && (
                <ContentSection>
                  <h2 className={styles.h2}>{translations.sharedByRealtor}</h2>
                  {sharedByRealtorDocuments?.map((item, index) => {
                    return (
                      <ActivityItem
                        key={`documents-${index}`}
                        status={item.status}
                        id={item.checklistItemId ?? undefined}
                        activityActions={
                          <Button
                            label={translations.downloadActionLabel}
                            onClick={() => handleDownload(item.id || "")}
                            buttonTheme={ButtonThemes.Primary}
                            size="s"
                          />
                        }
                        title={item.fileName || ""}
                        icon={faFileLines}
                      />
                    );
                  })}
                </ContentSection>
              )}

              {Number(sharedByUserDocuments?.length) > 0 && (
                <ContentSection>
                  <h2 className={styles.h2}>{translations.sharedByYou}</h2>
                  {sharedByUserDocuments?.map((item, index) => {
                    return (
                      <ActivityItem
                        key={`documents-${index}`}
                        status={item.status}
                        id={item.checklistItemId ?? undefined}
                        activityActions={
                          <Button
                            label={translations.downloadActionLabel}
                            onClick={() => handleDownload(item.id || "")}
                            buttonTheme={ButtonThemes.Primary}
                            size="s"
                          />
                        }
                        title={item.fileName || ""}
                        icon={faFileLines}
                      />
                    );
                  })}
                </ContentSection>
              )}
            </>
          )}
        </>
      )}
    </ScreenWrapper>
  );
};

export { DocumentsScreen };
